import { useCallback, useMemo, useState } from "react"
import { MainContentPaddingTypes, NewButton, NewButtonList, NewModal } from "../BaseComponents"
import { useTranslation } from "react-i18next"
import { Graph_QL, System, useSystem } from "../../Services";
import { colors, fonts, glyphs, images } from "../../Styles";
import { useAppSelector } from "../../Store";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (err) => reject(err);
  });
};

const MAX_FILE_SIZE = 500 * 1024;

interface BluePrintModalProps {
  system: System;
  onClose: () => void;
}

export default function BluePrintModal({system, onClose}:BluePrintModalProps) {
  const { t } = useTranslation();
  const [viewState, setViewState] = useState<"Blueprint" | "Upload" | null>(system.image !== null ? "Blueprint" : "Upload");
  
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFileName, setDroppedFileName] = useState<string | null>(null);
  const [error, setError] = useState<string>("")
  const [base64Data, setBase64Data] = useState<string|undefined>();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { maintainSystemImage } = useSystem(connection);

  // #region | Handlers
  // Handle Drag Over
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle Drag Leave
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle validation
  const validateAndSetFile = useCallback(async (file: File | null, source: "drop" | "input") => {
    if (!file) return;
    const array = new Uint8Array(await file.arrayBuffer());
    const start = Array.from(array.subarray(0,2)).map((value) => value.toString(16));
    const end = Array.from(array.subarray(-2)).map((value) => value.toString(16));
    
    // Magic number for jpeg are ff d8 for start and ff d9 for end.
    if (file.type !== "image/jpeg" || (start[0] !== "ff" && start[1] !== "d8" && end[0] !== "ff" && end[1] !== "d9" )) {
      setError(source === "drop" ? t("systemControls.blueprint.pleaseDropImage", {defaultValue: "Please drop a JPEG file" }) : t("systemControls.blueprint.pleaseDropImage", {defaultValue: "Please select a JPEG file" }));
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }
  
    if (file.size > MAX_FILE_SIZE) {
      setError(t("systemControls.blueprint.wrongSize", {defaultValue: "File is too large (max 500 kB)" }));
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }
  
    try {
      const result = await fileToBase64(file);
      setBase64Data(result);
    } catch (error) {
      setError(t("systemControls.blueprint.wrongType", {defaultValue: "Error processing file" }) );
      setDroppedFileName(null);
      setTimeout(() => setError(""), 2000);
      return;
    }

    setDroppedFileName(file.name);
  }, []);

  // Handle Drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSetFile(e.dataTransfer.files[0], "drop");
  }, [validateAndSetFile]);

  // Handle File Input
  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSetFile(e.target.files?.[0] || null, "input");
  }, [validateAndSetFile]);
  // #endregion

  return (
    <NewModal
      modalState={true}
      predefinedButtons={{ onClose: () => { setViewState(null); onClose()} }}
      header={t("systemControls.blueprint.blueprint", { defaultValue: "blueprint" })}
      subHeader={system.name}
      overrides={{
        mainContentPadding: viewState === "Blueprint" ? "0px" : MainContentPaddingTypes.secondary
      }}
      tabButtons={{
        contents: [t("systemControls.blueprint.blueprint", { defaultValue: "blueprint" }), t("systemControls.blueprint.upload", { defaultValue: "upload" })],
        onClick: (i) => {
          if (i === 0) { setViewState("Blueprint") }
          if (i === 1) { setViewState("Upload") }
        },
        startEnhancers: [glyphs.BluePrint({}), glyphs.Upload({})],
        disabled: viewState === "Blueprint" ? 0 : 1,
        active: viewState === "Blueprint" ? 0 : 1,
      }}
      mainContent={
        viewState === "Blueprint" ? <div style={{width: "100%", height: "100%", overflow: "scroll", scrollbarColor: colors.blueDarkPrimary+colors.transparent, scrollbarWidth: "thin"}}><img src={system.image ? "data:image/jpeg;base64," + system.image : images.systemView} alt={`${system?.name} ${t("systemControls.blueprint.blueprint", { defaultValue: "blueprint" })}`} style={{ maxWidth: "1000px", borderRadius: "0px 0px 10px 10px", display: "block" }} /></div>
        : <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} style={{
          width: "350px",
          height: "250px",
          borderRadius: "10px",
          borderWidth: "3px",
          borderStyle: "dashed",
          borderColor: isDragging ? colors.greyLightTertiary : colors.blueDarkSecondary,
          boxSizing: "border-box",
          margin: "0px auto",
          transition: "border-color 0.3s ease",
        }}>
          {!droppedFileName ? (<div style={{opacity: isDragging ? "0.2" : undefined, height: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px", transition: "opacity 0.3s ease"}}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
              <glyphs.Upload size="40px"/>
              <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize3, ...fonts.paragraph }}>
              {t("systemControls.blueprint.dropImage", { defaultValue: "Drag and drop your blueprint here" })}!
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", position: "relative" }}>
              <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.paragraph }}>
                {t("systemControls.blueprint.browseImage", { defaultValue: "Or browse it from your computer" })}:
              </p>
              <NewButton
                content={t("systemControls.blueprint.chooseFile", { defaultValue: "choose file" })}
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = "image/jpeg";
                  input.onchange = handleFileInput as unknown as (this: GlobalEventHandlers, ev: Event) => any;
                  input.click();
                }}
              />
              <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.header, position: "absolute", bottom: "-23px" }}> {error} </p>
            </div>
          </div> ) : ( <div style={{height: "100%", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "20px"}}>
            <p style={{ color: colors.greyLightTertiary, ...fonts.fontSize4, ...fonts.paragraph }}> {droppedFileName} </p>
            <NewButtonList
              contents={[t("buttons.save", {defaultValue: "save"}), t("systemControls.blueprint.chooseAgain", { defaultValue: "choose again" })]}
              onClick={(i) => {
                if (i === 0) {
                  maintainSystemImage(system.system_id, base64Data);
                  setViewState("Blueprint")
                }
                if (i === 1) setDroppedFileName(null);
              }}
              overrides={{
                baseStyles: {width: "130px"}
              }}
            />
          </div> )}
        </div>
        }
    />
  )
}